import React, { useState } from 'react';

import { Alert, Stack, Group, Button } from '@mantine/core';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';

import AddPaymentMethod from './AddPaymentMethod';
import SelectPaymentMethod from './SelectPaymentMethod';
import { useCheckout } from '../../CheckoutContext';

type PaymentMode = 'select' | 'add';

// The Payment step of the checkout accordion. When the billing plan does not
// require a payment method the whole step is skipped by CheckoutContext (F7),
// so this component only renders for plans that do.
const PaymentInformation = (): JSX.Element => {
  const { form, billingPlan, completeStep, paymentStepError } = useCheckout();
  const [mode, setMode] = useState<PaymentMode>('select');

  const hasSelectedOrRegisteredPaymentMethod =
    !!form.values.paymentMethod.selectedPaymentMethodId ||
    !!form.values.paymentMethod.stripeConfirmationToken;

  // A $0 plan that still requires a payment method is an owner misconfiguration
  // (spec Item 3): explain the unexpected card request instead of leaving it silent.
  const isZeroPricePlanRequiringPaymentMethod = (billingPlan.totalPrice ?? 0) === 0;

  return (
    <Stack>
      {/* Spec Item 9: a saved card declined at charge time surfaces here, inline, so the
          buyer can pick another saved method or add a new one without leaving checkout. */}
      {paymentStepError && (
        <Alert
          icon={<IconAlertCircle size='1rem' />}
          color='red'
          title="Payment didn't go through"
          data-cy='paymentStepError'
        >
          {paymentStepError} Select a different saved payment method or add a new one to try again.
        </Alert>
      )}

      {isZeroPricePlanRequiringPaymentMethod && (
        <Alert icon={<IconInfoCircle size='1rem' />} color='blue' data-cy='zeroPricePaymentMethodNotice'>
          A payment method is required even for this package.
        </Alert>
      )}

      {mode === 'add' ? (
        <AddPaymentMethod onDone={() => setMode('select')} />
      ) : (
        <>
          <SelectPaymentMethod onAddPaymentMethod={() => setMode('add')} />
          <Group position='right'>
            <Button
              data-cy='paymentContinue'
              onClick={() => completeStep('payment')}
              disabled={!hasSelectedOrRegisteredPaymentMethod}
            >
              Continue
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
};

export default PaymentInformation;
